import React from 'react';
import { Drawer, IconButton, Button } from '@mui/material';
import { Close, Delete, Remove, Add, ErrorOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import './Cart.styled.scss';
import { TCartComponent } from './Cart.types';
import { APP_ROUTES } from '../../../constants/routes';
import { useLocalCart } from '../../../hooks/useLocalCart';
import { useCheckoutSession } from '../../../hooks/useCheckoutSession';
import { useAppDispatch } from 'store/store';
import {
  abortStockReservationAsyncThunk,
  getStockListByIdsAsyncThunk,
} from 'store/stock/stock.action';
import { TStockItem } from 'api/stock/stock.types';
import {
  checkCartStockFulfillment,
  getMissingStockItems,
  hasMissingStockItems,
} from './Cart.utils';

export const Cart: TCartComponent = ({ open, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    items: cartItems,
    total: subtotal,
    updateQuantity,
    removeItem,
  } = useLocalCart();
  const { session, clearSession: clearCheckoutSession } = useCheckoutSession();

  const [stockVocabulary, setStockVocabulary] = React.useState<
    Record<TStockItem['id'], TStockItem>
  >({});

  const stockItemsInCart = React.useMemo(() => {
    return cartItems?.reduce?.(
      (acc, cartItem) => {
        cartItem.stock?.forEach((stock) => {
          if (!stock.stockItem?.id) {
            return;
          }
          if (!acc[stock.stockItem?.id]) {
            acc[stock.stockItem?.id] = stock.quantity * cartItem.quantity;
          } else {
            acc[stock.stockItem?.id] += stock.quantity * cartItem.quantity;
          }
        });
        return acc;
      },
      {} as Record<string, number>
    );
  }, [cartItems]);

  const stockItemsInCartFulfillment = React.useMemo(() => {
    return checkCartStockFulfillment(stockItemsInCart, stockVocabulary);
  }, [stockItemsInCart, stockVocabulary]);

  const missingStockItems = React.useMemo(() => {
    return getMissingStockItems(stockItemsInCart, stockVocabulary);
  }, [stockItemsInCart, stockVocabulary]);

  const hasMissingStockItemsInCart = React.useMemo(() => {
    return Object.keys(missingStockItems).length > 0;
  }, [missingStockItems]);

  const FREE_SHIPPING_THRESHOLD = 200;
  const freeShippingUnlocked = subtotal >= FREE_SHIPPING_THRESHOLD;
  const freeShippingProgress = Math.min(
    (subtotal / FREE_SHIPPING_THRESHOLD) * 100,
    100
  );
  const remainingForFreeShipping = Math.max(
    0,
    FREE_SHIPPING_THRESHOLD - subtotal
  );

  const handleQuantityChange = (itemId: string, delta: number) => {
    const item = cartItems.find((item) => item.id === itemId);
    if (item) {
      const newQuantity = Math.max(1, item.quantity + delta);
      updateQuantity(itemId, newQuantity);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    removeItem(itemId);
  };

  const doCheckoutAttempt = () => {
    clearCheckoutSession();
    dispatch(
      getStockListByIdsAsyncThunk({
        queryParams: {
          idList: Object.keys(stockItemsInCart),
        },
        onSuccess: (stockItems) => {
          const stockItemsVocabularyIncoming = stockItems.reduce<
            Record<TStockItem['id'], TStockItem>
          >((acc, item) => {
            acc[item.id] = item;
            return acc;
          }, {});
          setStockVocabulary(stockItemsVocabularyIncoming);

          const isStockFulfillment = checkCartStockFulfillment(
            stockItemsInCart,
            stockItemsVocabularyIncoming
          );
          if (isStockFulfillment) {
            onClose();
            navigate(APP_ROUTES.CHECKOUT);
            setStockVocabulary({});
          } else {
            // toast.error('Some items are out of stock');
          }
        },
      })
    );
  };

  const handleCheckout = () => {
    const reservationId = session?.checkoutData.reservationId;
    if (reservationId) {
      dispatch(
        abortStockReservationAsyncThunk({
          body: { id: reservationId },
          onSuccess: doCheckoutAttempt,
        })
      );
    } else {
      doCheckoutAttempt();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="cart-drawer"
      PaperProps={{
        className: 'cart-drawer__paper',
      }}
    >
      <div className="cart">
        {/* Header */}
        <div className="cart__header">
          <h6>My cart</h6>
          <IconButton
            onClick={onClose}
            className="cart__close-button"
            aria-label="close cart"
          >
            <Close />
          </IconButton>
        </div>

        <div className="cart__content">
          {/* Free Shipping Banner */}
          {freeShippingUnlocked ? (
            <div className="cart__banner cart__banner--success">
              <p>Congrats! You have unlocked free standard shipping!</p>
              <div className="cart__progress-bar">
                <div
                  className="cart__progress-bar-fill"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          ) : (
            <div className="cart__banner cart__banner--info">
              <p>
                Add {remainingForFreeShipping.toFixed(2)} zl. more for free
                standard shipping!
              </p>
              <div className="cart__progress-bar">
                <div
                  className="cart__progress-bar-fill"
                  style={{ width: `${freeShippingProgress}%` }}
                />
              </div>
            </div>
          )}
          {hasMissingStockItemsInCart ? (
            <div className="cart__banner cart__banner--error">
              <p>
                Unfortunately there are not enough items in our stock to
                checkout. Please consider adjust quantity of items that use
                these items in our stock:
              </p>
              <ol className="cart__stock-errors-list">
                {Object.entries(missingStockItems).map(([stockItemId]) => (
                  <li key={stockItemId}>
                    {[
                      stockVocabulary[stockItemId]?.name,
                      stockVocabulary[stockItemId]?.variation,
                    ].join(', ')}{' '}
                    - max {stockVocabulary[stockItemId]?.quantity ?? 0} in total
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
          {/* Cart Items */}
          <div className="cart__items">
            {cartItems.length === 0 ? (
              <div className="cart__empty">
                <p>Your cart is empty</p>
              </div>
            ) : (
              cartItems.map((item) => (
                <div key={item.id} className="cart__item">
                  <div className="cart__item-image">
                    <img src={item.productImage} alt={item.productName} />
                  </div>
                  <div className="cart__item-details">
                    <div className="cart__item-title-container">
                      <h4 className="cart__item-title-container__title">
                        {item.productName}
                      </h4>
                      {hasMissingStockItems(item.stock, missingStockItems) ? (
                        <ErrorOutline color="error" />
                      ) : null}
                    </div>

                    {item.stock?.map?.((stockEntry) => (
                      <span key={stockEntry.stockItem?.id}>
                        <p className="body2">
                          {[
                            stockEntry.stockItem?.name,
                            stockEntry.stockItem?.variation,
                          ].join(', ')}
                        </p>
                      </span>
                    ))}
                    <div className="cart__item-meta">
                      <span className="cart__item-variant">
                        {/* {Object.keys(item.customOptions).length > 0
                          ? formatValues(item.customOptions)
                          : 'Standard'} */}
                      </span>
                    </div>
                    <div className="cart__item-footer">
                      <div className="cart__item-price">
                        <span>
                          {(item.price * item.quantity).toFixed(2)} zl.
                        </span>
                      </div>
                      <div className="cart__item-controls">
                        <div className="cart__quantity-control">
                          <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(item.id, -1)}
                            className="cart__quantity-button"
                          >
                            <Remove fontSize="small" />
                          </IconButton>
                          <span className="cart__quantity-value">
                            {item.quantity}
                          </span>
                          <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(item.id, 1)}
                            className="cart__quantity-button"
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </div>
                        <IconButton
                          onClick={() => handleRemoveItem(item.id)}
                          className="cart__delete-button"
                          aria-label="remove item"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="cart__footer">
          <div className="cart__footer-content">
            <div className="cart__subtotal">
              <h4>Subtotal: {subtotal.toFixed(2)} zl.</h4>
              <p className="cart__subtotal-shipping body2">
                {freeShippingUnlocked
                  ? 'Free shipping'
                  : 'Shipping will be calculated on the next step'}
              </p>
            </div>
          </div>
          <Button
            variant="contained"
            size="large"
            fullWidth
            className="cart__checkout-button"
            disabled={cartItems.length === 0 || !stockItemsInCartFulfillment}
            onClick={handleCheckout}
          >
            START CHECKOUT →
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
